<template>
  <layout-public>
    <page-meta />
    <legal-document
      :label="t('Legal')"
      :title="t('Privacy Policy')"
      :subtitle="`${business.name} (${business.registration})`"
      :last-updated="t('2 October 2026')"
    >
      <p class="legal-intro">{{ t('This Privacy Policy is the full version of the Privacy Notice referred to in the {site} Service Agreement (Form B). The latest version is published at', parameters) }} <a :href="`https://${business.domain}`">{{ business.domain }}</a>.</p>

      <h2>{{ t('Privacy notice') }}</h2>

      <p>{{ t('By submitting this Service Agreement, you are providing personal information to {site}. {site} has the right to process your personal information provided in this Service Agreement and/or further information and data that may be required by {site} either from you or from any third parties. You hereby consent and authorise {site} to process and verify any information about you from any third party, which {site} may require in connection with your application for any of {site}\' services and/or review of the existing services with {site}.', parameters) }}</p>

      <p>{{ t('The information as required herein (unless stated otherwise) is obligatory on you and failure by you to provide any information as required may affect your service application or the continued provisioning of the services provided herein. In the course of processing this service application, your personal information may be shared where necessary and only on a need to know basis with other agencies so as to serve you in the most efficient and effective manner. Your personal information will not be disclosed to any unauthorised third party.') }}</p>

      <p>{{ t('{site} may also disclose your personal information if required to do so by law or in good faith, if such action is necessary to:', parameters) }}</p>
      <ol class="legal-list-alpha">
        <li>{{ t('comply with any law enforcement agency requirement, court orders or legal process or;') }}</li>
        <li>{{ t('protect and defend the rights or property of {site} and their users.', parameters) }}</li>
      </ol>
      <p>{{ t('{site} may review and update this Privacy Notice from time to time to reflect changes in the law, changes in our business practices, procedures and structure, and the community\'s changing privacy expectations. The latest and full version of the {site}\' Privacy Policy will be made available at', parameters) }} <a :href="`https://${business.domain}`">{{ business.domain }}</a>.</p>

      <!-- The assistant collects personal data on our own behalf, which nothing
           on the site disclosed while it was a third party's widget. It is
           written in the same register as the clauses above, and the retention
           it names is the same 180 days the prune command deletes on, so the
           document and the deletion cannot drift apart. -->
      <h2>{{ t('AI assistant') }}</h2>

      <p>{{ t('The chat window on this website is answered by an AI assistant. Using it means we hold the following record, which is our own and not a copy of anything you have sent us by other means:') }}</p>
      <ul>
        <li>{{ t('the conversation you have with the assistant, including the short summary of its thinking when it writes one, so that it can be answered and kept for your next visit;') }}</li>
        <li>{{ t('a phone number or an email address you give so the team can reply, together with a short excerpt of the recent messages, which we send to our own Telegram so the request can be seen;') }}</li>
        <li>{{ t('a random identifier in a cookie on your device, which is what holds those turns together as one conversation;') }}</li>
        <li>{{ t('the IP address and the web browser identification that your connection reports, recorded as reported and not treated as proof of who you are.') }}</li>
      </ul>

      <p>{{ t('Your question and the recent turns are sent to Cloudflare AI Gateway and on to the model provider that writes the answer. They process it for us and are not permitted to use it for their own purposes. The provider keeps its copy of a request for 30 days; we keep our own record for 180 days, after which it is deleted.') }}</p>

      <p>{{ t('We do not use this conversation for marketing, and we do not sell it. Apart from the short excerpt sent to our Telegram when a request needs the team, we do not pass the conversation on.') }}</p>

      <!-- Measurement on the public pages. Google Analytics 4 and the Meta
           Pixel each set cookies. Cloudflare Web Analytics counts the visit
           and the speed of the page and sets none. A referral link sets our
           own cookie for 90 days, the same window ReferralTracker::DAYS uses.
           An enquiry, or a chat request that asks the team to take over, can
           be attached to that code. A WhatsApp link can also place the
           referrer's name in the message the visitor sends. A lead from the
           enquiry form can carry
           the name and email on that form. The message is not included. -->
      <h2>{{ t('Website measurement') }}</h2>

      <p>{{ t('The public pages of this website use Google Analytics 4 and the Meta Pixel. They count visits, and which pages and contact actions are used. Each of them sets cookies on your device so that a later visit can be recognised as the same browser.') }}</p>

      <p>{{ t('We also use Cloudflare Web Analytics. It counts visits and how quickly a page loads, and it does not set a cookie.') }}</p>

      <p>{{ t('A link that names a referral sets a cookie on this website. The cookie holds a random identifier and the referral code, and we keep it for 90 days. We use it to count the visit and, when an enquiry is sent or the website chat asks the team to take over within those 90 days, to attach that enquiry or that chat request to the code. A WhatsApp link on this website can also place the referrer\'s name in the message you send, in the language of the page you are reading. We do not sell this record.') }}</p>

      <p>{{ t('When an enquiry form is accepted, the lead we record can include the name and the email address you typed on that form, so the enquiry can be matched to the visit that sent it. The message you wrote is not included. We do not sell this record.') }}</p>

      <h2>{{ t('Service notifications and marketing information') }}</h2>

      <p>{{ t('Unless otherwise specified in the Service Agreement, you agree to receive service notifications and marketing information from {site} via e-mail, telephone, and other formats where {site} deemed to be appropriate. You may, upon request, opt-out from the {site}\' marketing information.', parameters) }}</p>
    </legal-document>
  </layout-public>
</template>

<script>
import { useI18n } from 'vue-i18n';
import PageMeta from '@/Components/PageMeta.vue';
import LayoutPublic from '@/Layouts/Public.vue';
import LegalDocument from '@/Components/LegalDocument.vue';
import { privacyPolicyMessages } from '@/i18n/legal/privacyPolicy';

export default {
  components: {
    PageMeta,
    LayoutPublic,
    LegalDocument
  },

  computed: {
    business() {
      return this.$page.props.business;
    },

    /* The same parameters object the Terms of Use page passes: one place where
       the business name, registration number and domain enter the document. */
    parameters() {
      return {
        domain: this.business.domain,
        registration: this.business.registration,
        site: this.business.name
      };
    }
  },

  setup() {
    const { t } = useI18n({ messages: privacyPolicyMessages });

    return { t };
  },
};
</script>
